// src/components/research/EntityDataBlock.jsx
// Optionale Daten-Tabelle (aus CSV, danach editierbar) + Diagramm + SQL-Query
// (Datenherkunft) unterhalb des Eintrags-Texts.
//  - Lese-Modus: zeigt NUR das Diagramm, falls eines konfiguriert ist; sonst die
//    Tabelle. Darunter der SQL-Query als einklappbarer Block (zu Beginn zugeklappt);
//    ausgeklappt erscheinen unter dem Query die automatisch erkannten Tabellen.
//  - Edit-Modus: CSV hochladen/ersetzen, Tabelle inline bearbeiten (Zellen, Spalten-
//    namen, Zeilen/Spalten hinzufügen/löschen), Diagrammtyp + Spalten wählen
//    (Kuchen / Balken vertikal / Balken horizontal / gestapelt). Diagramm als Vorschau.
//    SQL-Query als Monospace-Textarea mit Live-Anzeige der erkannten Tabellen.
//
// Persistenz: Zell-/Spaltennamen-/SQL-Tippen geht über den Dirty-/Auto-Save-Pfad
// (onTableInput/onSqlInput + onCommit bei Blur); strukturelle Aktionen (Upload,
// Zeile/Spalte ±, Entfernen, Diagramm) persistieren sofort.

import React, { useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { extractSqlTables } from "../../services/research/sqlTables";
import { exportChartSvg } from "../../utils/exportChartSvg";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useTheme } from "../../contexts/ThemeContext";
import { dataTableTooLarge } from "../../services/azureResearchService";

const CHART_OPTIONS = [
  { value: "none", label: "Kein Diagramm" },
  { value: "pie", label: "Kuchendiagramm" },
  { value: "bar", label: "Balken (vertikal)" },
  { value: "barh", label: "Balken (horizontal)" },
  { value: "stacked", label: "Balken (gestapelt)" },
];

// Schweizer/DE-Zahlenformat tolerant parsen ("1'234,5" -> 1234.5). Sonst null.
const toNumber = (v) => {
  const s = String(v ?? "").replace(/['\s]/g, "").replace(",", ".");
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const isNumericColumn = (rows, ci) => {
  let seen = 0;
  let numeric = 0;
  for (const r of rows) {
    const v = r[ci];
    if (v === "" || v == null) continue;
    seen += 1;
    if (toNumber(v) !== null) numeric += 1;
  }
  return seen > 0 && numeric / seen >= 0.6;
};

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const asHex = (c, fallback) => (HEX_RE.test(String(c || "").trim()) ? String(c).trim() : fallback);

// Theme-Variablen für die Standard-Palette + Hex-Fallbacks (für den Color-Picker,
// der zwingend gültiges #rrggbb braucht).
const PALETTE_VARS = [
  "--main-color",
  "--accent-color",
  "--success-color",
  "--warning-color",
  "--error-color",
  "--text-color-secondary",
];
const FALLBACK_COLORS = ["#4f86c6", "#7c5cff", "#10b981", "#f59e0b", "#ef4444", "#888888"];

// Azure-Property-Limit ~32K Zeichen; etwas Reserve.
const MAX_SQL_CHARS = 30000;

// SQL-Query (Datenherkunft): Anzeige-/Editier-Block inkl. automatisch erkannter
// Tabellen. Read-Mode: einklappbar (Standard: zugeklappt); die Tabellen-Liste
// erscheint nur im ausgeklappten Zustand unterhalb des Querys.
const SqlQueryBlock = ({ sqlQuery, readOnly, onSqlInput, onCommit }) => {
  const hasQuery = !!(sqlQuery && sqlQuery.trim());
  // Edit-Mode: Textarea automatisch zeigen, sobald ein Query existiert.
  const [editorOpen, setEditorOpen] = useState(hasQuery);
  const [expanded, setExpanded] = useState(false);

  const tables = useMemo(() => extractSqlTables(sqlQuery), [sqlQuery]);

  const renderTables = () =>
    tables.length > 0 && (
      <div className="qna-sql-tables">
        <span className="qna-sql-tables-label">Verwendete Tabellen:</span>
        {tables.map((t) => (
          <span key={t} className="qna-sql-table-chip" title={`Datenquelle ${t}`}>
            {t}
          </span>
        ))}
      </div>
    );

  if (readOnly) {
    if (!hasQuery) return null;
    return (
      <div className="qna-sql">
        <button
          type="button"
          className="qna-sql-toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? "SQL-Query ausblenden" : "SQL-Query anzeigen"}
        </button>
        {expanded && (
          <>
            <pre className="qna-sql-code">{sqlQuery}</pre>
            {renderTables()}
          </>
        )}
      </div>
    );
  }

  if (!editorOpen && !hasQuery) {
    return (
      <div className="qna-sql">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setEditorOpen(true)}
          title="SQL-Query hinterlegen, der dokumentiert, wie die Daten zustande gekommen sind"
        >
          SQL-Query hinterlegen
        </button>
      </div>
    );
  }

  return (
    <div className="qna-sql">
      <div className="qna-sql-edit-label">SQL-Query (Datenherkunft)</div>
      <textarea
        className="qna-sql-input"
        value={sqlQuery || ""}
        maxLength={MAX_SQL_CHARS}
        spellCheck={false}
        rows={Math.min(18, Math.max(4, (sqlQuery || "").split("\n").length + 1))}
        placeholder={"SELECT …\nFROM …\nJOIN … ON …\n-- formatiert einfügen; dokumentiert, wie die Daten entstanden sind"}
        onChange={(e) => onSqlInput(e.target.value)}
        onBlur={onCommit}
      />
      {renderTables()}
    </div>
  );
};

const EntityDataBlock = ({
  dataTable,
  chart,
  sqlQuery,
  exportName,
  readOnly,
  onSetTable,
  onTableInput,
  onCommit,
  onClearTable,
  onSetChart,
  onChartInput,
  onSqlInput,
}) => {
  const { getComputedThemeValue, currentTheme } = useTheme();
  const [localError, setLocalError] = useState("");
  const chartRef = useRef(null);

  const columns = dataTable?.columns || [];
  const rows = dataTable?.rows || [];
  const hasTable = columns.length > 0;
  const cfg = chart && chart.type !== "none" ? chart : null;
  const hasChart = !!cfg && cfg.valueCols.length > 0;

  // Standard-Farbpalette aus den Theme-Variablen (recharts braucht echte Farbstrings).
  const palette = useMemo(
    () => PALETTE_VARS.map((v, i) => getComputedThemeValue(v) || FALLBACK_COLORS[i]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getComputedThemeValue, currentTheme]
  );

  // Effektive Farbe an Position i: eigene Wahl (chart.colors) sonst Theme-Palette.
  const colorAt = (i) => (cfg && cfg.colors && cfg.colors[i]) || palette[i % palette.length];

  const axisColor = useMemo(
    () => getComputedThemeValue("--text-color") || "#888",
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getComputedThemeValue, currentTheme]
  );

  const numericCols = useMemo(
    () => columns.map((_, i) => i).filter((i) => isNumericColumn(rows, i)),
    [columns, rows]
  );

  // --- CSV-Upload (ersetzt die Tabelle) -------------------------------------
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // erlaubt erneutes Hochladen derselben Datei
    if (!file) return;
    try {
      const text = await file.text();
      const wb = XLSX.read(text, { type: "string" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: "" });
      if (!aoa.length) {
        setLocalError("Die Datei ist leer.");
        return;
      }
      const cols = (aoa[0] || []).map((c, i) => String(c ?? "").trim() || `Spalte ${i + 1}`);
      const width = cols.length;
      const newRows = aoa
        .slice(1)
        .map((r) => Array.from({ length: width }, (_, i) => String(r[i] ?? "")));
      const table = { columns: cols, rows: newRows };
      if (dataTableTooLarge(table)) {
        setLocalError("Datei zu gross (max. ~150 KB). Bitte verkleinern.");
        return;
      }
      setLocalError("");
      onSetTable(table);
    } catch {
      setLocalError("CSV konnte nicht gelesen werden.");
    }
  };

  // --- Inline-Editing -------------------------------------------------------
  // Zell-/Spaltennamen-Tippen: lokal + dirty (onTableInput), Persistenz bei Blur.
  const editHeader = (ci, val) =>
    onTableInput({ columns: columns.map((c, i) => (i === ci ? val : c)), rows });

  const editCell = (ri, ci, val) =>
    onTableInput({
      columns,
      rows: rows.map((r, i) => (i === ri ? r.map((c, j) => (j === ci ? val : c)) : r)),
    });

  // Strukturelle Aktionen: sofort persistieren.
  const addRow = () => onSetTable({ columns, rows: [...rows, columns.map(() => "")] });
  const deleteRow = (ri) => onSetTable({ columns, rows: rows.filter((_, i) => i !== ri) });

  const addColumn = () =>
    onSetTable({
      columns: [...columns, `Spalte ${columns.length + 1}`],
      rows: rows.map((r) => [...r, ""]),
    });

  const deleteColumn = (ci) => {
    onSetTable({
      columns: columns.filter((_, i) => i !== ci),
      rows: rows.map((r) => r.filter((_, i) => i !== ci)),
    });
    // Diagramm-Spaltenindizes nachziehen, sonst zeigt es auf falsche Spalten.
    if (cfg) {
      if (cfg.labelCol === ci) {
        onSetChart(null); // Kategorie-Spalte entfernt -> Diagramm zurücksetzen
      } else {
        const shift = (i) => (i > ci ? i - 1 : i);
        onSetChart({
          ...cfg,
          labelCol: shift(cfg.labelCol),
          valueCols: cfg.valueCols.filter((i) => i !== ci).map(shift),
        });
      }
    }
  };

  // --- Diagramm-Konfiguration ----------------------------------------------
  const autoConfig = (type) => {
    const labelCol = columns.map((_, i) => i).find((i) => !numericCols.includes(i)) ?? 0;
    let valueCols = numericCols.filter((i) => i !== labelCol);
    if (valueCols.length === 0) valueCols = numericCols.slice();
    if (type === "pie") valueCols = valueCols.slice(0, 1);
    return { type, labelCol, valueCols };
  };

  const changeType = (type) => {
    if (type === "none") {
      onSetChart(null);
      return;
    }
    if (!cfg) {
      onSetChart(autoConfig(type));
    } else {
      let valueCols = cfg.valueCols.length ? cfg.valueCols : autoConfig(type).valueCols;
      if (type === "pie") valueCols = valueCols.slice(0, 1);
      onSetChart({ type, labelCol: cfg.labelCol, valueCols });
    }
  };

  const changeLabelCol = (ci) => cfg && onSetChart({ ...cfg, labelCol: ci });

  const toggleValueCol = (ci) => {
    if (!cfg) return;
    if (cfg.type === "pie") {
      onSetChart({ ...cfg, valueCols: [ci] });
      return;
    }
    const set = new Set(cfg.valueCols);
    if (set.has(ci)) set.delete(ci);
    else set.add(ci);
    onSetChart({ ...cfg, valueCols: Array.from(set).sort((a, b) => a - b) });
  };

  // Farbe an Position i setzen (live, dirty) bzw. alle Eigenfarben zurücksetzen.
  const setColor = (i, hex) => {
    if (!cfg) return;
    const colors = [...(cfg.colors || [])];
    colors[i] = hex;
    onChartInput({ ...cfg, colors });
  };
  const resetColors = () => cfg && onSetChart({ ...cfg, colors: [] });

  // --- Diagramm-Daten -------------------------------------------------------
  const barData = useMemo(() => {
    if (!cfg) return [];
    return rows.map((r) => {
      const o = { __label: String(r[cfg.labelCol] ?? "") };
      cfg.valueCols.forEach((ci) => {
        o[columns[ci]] = toNumber(r[ci]) ?? 0;
      });
      return o;
    });
  }, [cfg, rows, columns]);

  const pieData = useMemo(() => {
    if (!cfg || cfg.valueCols.length === 0) return [];
    const ci = cfg.valueCols[0];
    return rows.map((r) => ({ name: String(r[cfg.labelCol] ?? ""), value: toNumber(r[ci]) ?? 0 }));
  }, [cfg, rows, columns]);

  const renderChart = () => {
    if (!hasChart) return null;
    const tick = { fill: axisColor, fontSize: 11 };

    if (cfg.type === "pie") {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" outerRadius="80%" label>
              {pieData.map((_, i) => (
                <Cell key={i} fill={colorAt(i)} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    const horizontal = cfg.type === "barh";
    const stacked = cfg.type === "stacked";
    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={barData}
          layout={horizontal ? "vertical" : "horizontal"}
          margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          {horizontal ? (
            <>
              <XAxis type="number" tick={tick} />
              <YAxis type="category" dataKey="__label" tick={tick} width={120} />
            </>
          ) : (
            <>
              <XAxis dataKey="__label" tick={tick} />
              <YAxis type="number" tick={tick} />
            </>
          )}
          <Tooltip />
          <Legend />
          {cfg.valueCols.map((ci, idx) => (
            <Bar
              key={ci}
              dataKey={columns[ci]}
              fill={colorAt(idx)}
              stackId={stacked ? "s" : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const handleExportSvg = (e) => {
    e.stopPropagation();
    if (!exportChartSvg(chartRef.current, exportName)) {
      setLocalError("Diagramm konnte nicht exportiert werden.");
    }
  };

  const renderExportButton = () => (
    <button
      type="button"
      className="btn btn-secondary btn-sm"
      onClick={handleExportSvg}
      onDoubleClick={(e) => e.stopPropagation()}
      title="Diagramm als SVG-Datei herunterladen"
    >
      SVG exportieren
    </button>
  );

  const renderTable = (editable) => {
    if (!hasTable) return null;
    return (
      <div className="qna-data-table-wrap">
        <table className="qna-data-table">
          <thead>
            <tr>
              {columns.map((c, ci) => (
                <th key={ci}>
                  {editable ? (
                    <div className="qna-data-th-edit">
                      <input
                        value={c}
                        onChange={(e) => editHeader(ci, e.target.value)}
                        onBlur={onCommit}
                      />
                      <button
                        type="button"
                        className="qna-data-coldel"
                        title="Spalte löschen"
                        onClick={() => deleteColumn(ci)}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    c
                  )}
                </th>
              ))}
              {editable && <th className="qna-data-addcol-cell" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri}>
                {columns.map((_, ci) => (
                  <td key={ci}>
                    {editable ? (
                      <input
                        value={r[ci] ?? ""}
                        onChange={(e) => editCell(ri, ci, e.target.value)}
                        onBlur={onCommit}
                      />
                    ) : (
                      r[ci]
                    )}
                  </td>
                ))}
                {editable && (
                  <td className="qna-data-rowdel-cell">
                    <button
                      type="button"
                      className="qna-data-rowdel"
                      title="Zeile löschen"
                      onClick={() => deleteRow(ri)}
                    >
                      ×
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {editable && (
          <div className="qna-data-struct">
            <button type="button" className="btn btn-secondary btn-sm" onClick={addRow}>
              + Zeile
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addColumn}>
              + Spalte
            </button>
          </div>
        )}
      </div>
    );
  };

  // --- Lese-Modus: Diagramm falls vorhanden, sonst Tabelle; darunter SQL ----
  if (readOnly) {
    const hasSql = !!(sqlQuery && sqlQuery.trim());
    if (!hasTable && !hasSql) return null;
    return (
      <div className="qna-data">
        {hasTable &&
          (hasChart ? (
            <>
              <div ref={chartRef}>{renderChart()}</div>
              <div className="qna-data-chart-actions">
                {renderExportButton()}
                {localError && <span className="qna-data-error">{localError}</span>}
              </div>
            </>
          ) : (
            renderTable(false)
          ))}
        <SqlQueryBlock sqlQuery={sqlQuery} readOnly onCommit={onCommit} />
      </div>
    );
  }

  // --- Edit-Modus -----------------------------------------------------------
  return (
    <div className="qna-data">
      <div className="qna-data-toolbar">
        <label className="btn btn-secondary btn-sm qna-data-upload">
          {hasTable ? "CSV ersetzen" : "CSV hochladen"}
          <input type="file" accept=".csv,text/csv" onChange={handleFile} hidden />
        </label>
        {hasTable && (
          <>
            <select
              className="qna-data-select"
              value={cfg?.type || "none"}
              onChange={(e) => changeType(e.target.value)}
              title="Diagrammtyp"
            >
              {CHART_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {hasChart && renderExportButton()}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => onClearTable()}
              title="Tabelle entfernen"
            >
              Tabelle entfernen
            </button>
          </>
        )}
        {localError && <span className="qna-data-error">{localError}</span>}
      </div>

      {/* Diagramm-Spaltenwahl (nur bei aktivem Diagrammtyp) */}
      {hasTable && cfg && (
        <div className="qna-data-config">
          <label className="qna-data-config-row">
            <span>Kategorie-Spalte:</span>
            <select
              className="qna-data-select"
              value={cfg.labelCol}
              onChange={(e) => changeLabelCol(Number(e.target.value))}
            >
              {columns.map((c, i) => (
                <option key={i} value={i}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <div className="qna-data-config-row">
            <span>{cfg.type === "pie" ? "Wert-Spalte:" : "Wert-Spalten:"}</span>
            <div className="qna-data-valuecols">
              {columns.map((c, i) =>
                i === cfg.labelCol ? null : (
                  <label key={i} className="qna-data-valuecol">
                    <input
                      type={cfg.type === "pie" ? "radio" : "checkbox"}
                      name="valcol"
                      checked={cfg.valueCols.includes(i)}
                      onChange={() => toggleValueCol(i)}
                    />
                    {c}
                  </label>
                )
              )}
            </div>
          </div>
          {hasChart && (
            <div className="qna-data-config-row">
              <span>Farben:</span>
              <div className="qna-data-colors">
                {(cfg.type === "pie"
                  ? pieData.map((d, i) => ({ i, label: d.name || `Segment ${i + 1}` }))
                  : cfg.valueCols.map((ci, idx) => ({ i: idx, label: columns[ci] }))
                ).map(({ i, label }) => (
                  <label key={i} className="qna-data-color" title={label}>
                    <input
                      type="color"
                      value={asHex(colorAt(i), FALLBACK_COLORS[i % FALLBACK_COLORS.length])}
                      onChange={(e) => setColor(i, e.target.value)}
                      onBlur={onCommit}
                    />
                    <span>{label}</span>
                  </label>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" onClick={resetColors}>
                  Farben zurücksetzen
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Diagramm-Vorschau */}
      {hasChart && (
        <div className="qna-data-chart" ref={chartRef}>
          {renderChart()}
        </div>
      )}

      {/* Editierbare Tabelle */}
      {renderTable(true)}

      {/* SQL-Query (Datenherkunft) */}
      <SqlQueryBlock
        sqlQuery={sqlQuery}
        readOnly={false}
        onSqlInput={onSqlInput}
        onCommit={onCommit}
      />
    </div>
  );
};

export default EntityDataBlock;
